import { LitElement, html, css, PropertyValues } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { GetFormattedDate } from '../../../src/utils/helpers';
import { VoyageStyle } from '../../voyage-style/src/voyage-style';
import { TimeZone } from './voy-tide-info-popup';

interface DataSerie
{
    name: String,
    type: String,
    smooth: Boolean,
    color: String,
    showSymbol: Boolean,
    hoverAnimation: Boolean,
    data: number[][],
    markLine?: any,
    id: string,
    symbolSize: number
}

@customElement('voy-tide-graph')
export class VoyTideGraph extends LitElement {
    @property({type: String}) unit: string = "";
    @property({type: Array}) tideData: number[][] = [];
    @property({type: Number}) fromDate: number = -1;
    @property({type: Number}) toDate: number = -1;
    @property({type: Number}) minHeight: number = 2.5;
    @property({type: Object}) timeZone: TimeZone = { label: "UTC 0", offset: 0 };
    @property({attribute: false}) private _minHeightIntervals: any[] = [];

    private _chart: any;
    private _dataSeries: DataSerie[] | null = null;
    private _afId: number = 0; 
    private _chartElement: HTMLDivElement | null = null;
    private _noDataElement: HTMLDivElement | null = null;
    private _setMarkLinesRefresh: number | undefined;
    
    constructor() {
        super();

        this._initialize = this._initialize.bind(this);
        this.onResize = this.onResize.bind(this);
    }

    static get styles() {
        return [
            css`
            :host {
                width: 100%;
                height: 100%;
            }
            .hassc {
                cursor: pointer;
            }

            .updated {
                color: var(--neutrals-grey-04, black);
                font-size: 10px;
                text-align: right;
            }

            .graph {
                background: var(--neutrals-grey-10);
                height: 50vh;
                width: 80vw;
            }

            .container {
                font-family: 'MaerskText';
                display: grid;
                place-content: center;
            }

            #no-data {
                color: var(--neutrals-grey-03, black);
                font-size: 16px;
            }
        `];
    }

    render() {
        return html`
            <div class="container" id="container-div">
                <div class="graph" id="plot"></div>
                <div class="graph" id="no-data">No Data Found</div>
            </div>
        `;
    }

    getImage() {
        return this._chart.getDataURL({ backgroundColor: '#fff' });
    }

    updated(changedProperties: PropertyValues) {
        if (!this._chart) return;

        if (!this.tideData || !this.tideData.length) {
            this._showNoDataInformation();
            return;
        }

        this._showChart();
        this._chart.resize();
        
        changedProperties.forEach((_, propName) => {
            if (propName === 'unit') {
                this._chart.setOption({
                    yAxis: {
                        name: this.unit
                    }
                });
            } else if (propName === 'tideData') {
                this._dataSeries![0].data = this.tideData;
                
                this._chart.setOption({
                    series: this._dataSeries
                });
            } else if (propName === 'minHeight') {
                this._setMarkLines();
            }
        });
    }

    private _setMarkLines() {
        if (!this.tideData) {
            return;
        }
        
        let data = Array.from(this.tideData);
        const intervals = [];
                
        for (let i = 0; i< data.length - 1; i++) {
            if (data[i][1] < this.minHeight && data[i+1][1] >= this.minHeight ) {
                intervals.push(data[i+1][0]);
            }
            if (data[i][1] > this.minHeight && data[i+1][1] <= this.minHeight ) {
                intervals.push(data[i][0]);
            }
        }

        this._minHeightIntervals = intervals.map(i => 
            { return { 
                        xAxis: i, 
                        label: { show: true,  
                                 formatter: (valueObject: any) => {
                                    const dateVal = new Date(valueObject.value + this.timeZone.offset) ;
                                    const timeOfDay = `${dateVal.getUTCHours().toString().padStart(2,'0')}:${dateVal.getUTCMinutes().toString().padStart(2,'0')}`;
                                    return `${timeOfDay}`;
                               }},
                        lineStyle: {
                            width: 2
                        }
                    }
        });

        this._chart.setOption({
            series: {
                id: "tideData",
                markLine: this._minHeightMarkLine(),
            }        
        });
    }

    private _minHeightMarkLine() {
        const computedStyle = getComputedStyle(this);
        return {
            data: [
                ...this._minHeightIntervals,
                { xAxis: Date.now() + this.timeZone.offset,
                  label: { show: true,  formatter: (valueObject: any) => {
                    return `Now ${this.timeZone.label}` ;
                  }},
                  lineStyle: {
                    width: 3,
                    color: computedStyle.getPropertyValue("--neutrals-grey-03"),
                  }
                },
                { yAxis: this.minHeight,
                  label: { show: true, position: "middle",  formatter: (valueObject: any) => {
                      return `Window of opportunity ${valueObject.value.toFixed(2)} m` ;
                  }},
                  lineStyle: {
                      width: 2
                  }
                },
                
            ],
            animation: false, symbol: ["none", "none"]
        };
    }

    onResize() {
        if (this._afId !== 0) {
            return;
        }

        // Wait for the next frame, otherwise the size may not be ready yet
        this._afId = requestAnimationFrame( () => {
            this._chart.resize();
            this._afId = 0;
        });
    }

    get chart() : any {
        return this._chart;
    }

    _initialize() {
        const computedStyle = getComputedStyle(this);

        this._dataSeries = [{
            name: 'Height',
            type: 'line',
            smooth: true,
            color: computedStyle.getPropertyValue('--tide-data-color') || 'green',
            showSymbol: false,
            hoverAnimation: false,
            data: this.tideData,
            id: "tideData",
            symbolSize: 10
        }];
        
        this._chartElement = this.shadowRoot!.querySelector('#plot');
        
        if (!this._chart) {
            // @ts-ignore
            this._chart = echarts.init(this._chartElement);
        } else {
            this._chart.clear();
        }
        
        this._chart.setOption({
            title: {
                text: '',
            },
            textStyle: {
                color: computedStyle.getPropertyValue("--neutrals-grey-04"),
                fontSize: 12,
                fontFamily: 'MaerskText'
            },
            grid: {
                top: 25,
                bottom: 35,
                right: 20,
                left: 65
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    
                    type: "cross",
                    color: computedStyle.getPropertyValue("--neutrals-grey-04"),
                    opacity: 0.5,
                    
                    label: {
                        formatter: (valueObject: any, index: any) => {
                            if (valueObject.axisDimension === "x") {
                                return `${GetFormattedDate(valueObject.value + this.timeZone.offset)}`;
                            }

                            return valueObject.value.toFixed(2);
                        },
                    }
                },
                formatter: (params: any[]) => {
                    let result = '';
                    const colorSpan = (color: string) => '<span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background-color:' + color + '"></span>';
                    params.forEach(param => {
                        result += colorSpan(param.color) + ' ' + param.seriesName + ': ' + (Math.round(param.value[1] * 10) / 10) + '<br>';
                    });

                    return `${GetFormattedDate(params[0].value[0] + this.timeZone.offset)}<br>
                           ${result}`;
                }
            },
            dataZoom: [{
                type: 'inside'
            }, {
                type: 'slider'
            }],
            xAxis: {
                type: 'time',
                axisLabel: {
                    formatter: (value: string | number | Date, index: any) => {
                        var date = new Date(value);
                        const month = date.toLocaleString('en-us', { month: 'short', timeZone: 'UTC' });
                        return `${date.getUTCDate()} ${month}`;
                    }
                }
            },
            yAxis: {
                nameRotate: 90,
                nameLocation: 'middle',
                nameTextStyle: {
                    color: computedStyle.getPropertyValue("--neutrals-grey-04"),
                    fontSize: 12,
                    fontFamily: 'MaerskText',
                    padding: 12
                },
                axisLabel: {
                    showMinLabel: false,
                    showMaxLabel: false
                },
                name: this.unit,
                nameGap: 30,
                min: function (value: any) {
                    return (value.min - (value.max - value.min)*0.15).toFixed(2);
                },
                max: function (value: any) {
                    return (value.max + (value.max - value.min)*0.15).toFixed(2);
                },
            },
            responsive: true,
            maintainAspectRatio: false,
            series: this._dataSeries,
        })        

        this._setMarkLines();
    }

    _showNoDataInformation() {
        this._chartElement = this.shadowRoot!.querySelector('#plot');
        this._noDataElement = this.shadowRoot!.querySelector('#no-data');

        this._noDataElement!.style.display = 'block';
        this._chartElement!.style.display = 'none';
    }

    _showChart() {
        this._chartElement = this.shadowRoot!.querySelector('#plot');
        this._noDataElement = this.shadowRoot!.querySelector('#no-data');
        
        this._noDataElement!.style.display = 'none';
        this._chartElement!.style.display = 'block';
    }

    firstUpdated() {
        this._initialize();
        // @ts-ignore
        const ro = new ResizeObserver( () => this.onResize() );

        ro.observe(this);
        this.onResize();
        VoyageStyle.addEventListener('styles-changed', this._initialize);
    }

    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('resize', this.onResize);
        this._setMarkLinesRefresh = setInterval(this._setMarkLines, 1000*60);
    }

    disconnectedCallback() {
        VoyageStyle.removeEventListener('styles-changed', this._initialize);
        window.removeEventListener('resize', this.onResize);
        clearInterval(this._setMarkLinesRefresh);
    }
};
